import { WIDGET_DEFAULTS } from "@sagot/constants";
import { ChatTheme } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Progress } from "@sagot/ui";
import { useId, useRef } from "react";
import { LauncherMark } from "./launcher-mark";
import { useLauncherIconPicker, type LauncherIconStatus } from "./use-launcher-icon";
import { AVATAR_MIME_TYPES, brandContrast } from "./widget-model";

const K = "deployKnowledge.widgetStudio.appearance.iconUpload";

type TFunction = ReturnType<typeof useTranslation>["t"];

interface LauncherIconUploaderProps {
  agentId: string;
  iconUrl: string | null;
  /** The draft's brand colour: the mini launcher is drawn on it, as the widget will be. */
  color: string;
  onChange: (url: string | null) => void;
  disabled?: boolean;
}

function statusText(t: TFunction, status: LauncherIconStatus): string {
  if (status.kind === "busy") return t(`${K}.${status.stage}`, { name: status.name });
  if (status.kind === "ready") return t(`${K}.ready`, { name: status.name });
  return status.kind === "removed" ? t(`${K}.removed`) : "";
}

function StatusLine({ status, onRetry }: { status: LauncherIconStatus; onRetry: (file: File) => void }) {
  const { t } = useTranslation();
  if (status.kind === "error") {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs font-semibold text-danger">
        <span className="inline-flex items-start gap-1.5">
          <Icon name="alert-circle" className="mt-0.5" />
          <span>{status.message}</span>
        </span>
        {status.retry ? (
          <Button variant="secondary" size="sm" className="min-h-11" onClick={() => { if (status.retry) onRetry(status.retry); }}>
            <Icon name="refresh-cw" />
            {t(`${K}.retry`)}
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <p role="status" className="flex min-h-5 items-center gap-1.5 text-xs text-fg-muted">
      {status.kind === "ready" ? <Icon name="check-circle" className="text-success" /> : null}
      {statusText(t, status)}
    </p>
  );
}

/**
 * Widget Studio → Appearance → Launcher icon: replaces the chat-bubble glyph on the launcher (both
 * styles). Same upload path as the chat avatar; the live preview updates as soon as the draft does.
 */
export function LauncherIconUploader({ agentId, iconUrl, color, onChange, disabled }: LauncherIconUploaderProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const input = useRef<HTMLInputElement>(null);
  const picker = useLauncherIconPicker(agentId, onChange);
  const contrast = brandContrast(color);
  return (
    <div role="group" aria-labelledby={titleId}>
      <span id={titleId} className="label">
        {t(`${K}.title`)}
      </span>
      <div className="flex flex-wrap items-center gap-3">
        <ChatTheme primary={contrast.valid ? color : WIDGET_DEFAULTS.primary} onPrimary={contrast.onPrimary}>
          <span aria-hidden="true" data-testid="launcher-icon-preview" className="inline-flex size-14 shrink-0 items-center justify-center rounded-pill bg-[var(--wg-primary)] text-[var(--wg-on-primary)] shadow-pop">
            <LauncherMark iconUrl={iconUrl} size={40} glyphClassName="size-6" />
          </span>
        </ChatTheme>
        <input
          ref={input}
          type="file"
          data-upload="launcher-icon"
          accept={AVATAR_MIME_TYPES.join(",")}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            void picker.pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Button variant="secondary" className="min-h-11" aria-label={picker.busy ? undefined : t(`${K}.upload`)} disabled={disabled ?? false} loading={picker.busy} onClick={() => { if (!picker.busy) input.current?.click(); }}>
          <Icon name="upload" />
          {t(picker.busy ? "deployKnowledge.widgetStudio.appearance.uploading" : "deployKnowledge.widgetStudio.appearance.upload")}
        </Button>
        <Button variant="ghost" className="min-h-11" disabled={(disabled ?? false) || !iconUrl || picker.busy} onClick={picker.remove}>
          {t(`${K}.remove`)}
        </Button>
      </div>
      <p className="hint">{iconUrl ? t(`${K}.usingCustom`) : t(`${K}.usingDefault`)}</p>
      <p className="hint">{t(`${K}.hint`)}</p>
      <div className="mt-2 space-y-1.5">
        <StatusLine status={picker.status} onRetry={picker.send} />
        {picker.status.kind === "busy" && picker.status.stage === "uploading" ? (
          <Progress size="sm" value={picker.progress} label={statusText(t, picker.status)} valueText={`${String(picker.progress)}%`} />
        ) : null}
      </div>
    </div>
  );
}
